/**
 * Kinematics choir — soft multi-sine pad for swarm motion.
 *
 * Approximates the cloud as a whole: mean energy opens a warm triad,
 * clump pulls voices toward unison (settled murmur), spread opens the
 * fifth slightly. Pure sines + slow glide — never noise or grains.
 */

import type { SimParams } from "../../sim/params";
import type { AudioVoiceContext, AudioVoicePlugin } from "../../plugins/types";
import type { AudioFeatureFrame } from "../features";
import { clamp01, intervalHz, scaleHz, softGain, TAU } from "../music";
import type { AudioParams } from "../params";

/** Pure mapper for tests (no AudioContext). */
export function mapKinematicsPad(
  frame: AudioFeatureFrame,
  audio: AudioParams,
): {
  gain: number;
  rootHz: number;
  thirdHz: number;
  fifthHz: number;
  cutHz: number;
  shimmer: number;
} {
  const k = frame.kinematics;
  const g = frame.gravity;
  const top = frame.topology;

  // Whole-cloud energy — slow clouds still breathe a little so the pad
  // never drops to a hard mute when motion settles.
  const speedT = clamp01(k.meanSpeed / 0.04);
  const activity = 0.35 + 0.65 * clamp01(k.activeFrac);
  const clump = clamp01(g.clump);
  // Membership / structure sweetens presence when the graph is coherent
  const membership =
    0.15 * clamp01(top.nCycles / 8) + 0.1 * clamp01(1 - top.meanChainDepth / 14);
  const energy = speedT * activity * (0.55 + 0.45 * (0.5 + 0.5 * clump));
  const gain = softGain(0.28 + 0.72 * energy + membership, 0.12);

  // Melody rides mean speed; pitchAmount widens the walk on the scale.
  // Base ~A3 so it sits above the gravity bed and below harshness.
  const mel = speedT * 0.85 + 0.15 * clamp01(k.p90Speed / 0.07);
  const rootHz = scaleHz(mel, 57, 1.1, Math.max(0.15, audio.pitchAmount));

  // Clump → closer voicing (more unison / calmer). Spread / low clump →
  // a gentle open triad. Intervals stay consonant (m3 / P5 family).
  const open = (1 - clump) * (0.55 + 0.45 * clamp01(k.p90Speed / 0.08));
  const thirdSemis = 3 + 0.4 * open; // minor third, slightly wider when open
  const fifthSemis = 7 + 0.6 * open; // perfect fifth → mild stretch when open
  const thirdHz = intervalHz(rootHz, thirdSemis);
  const fifthHz = intervalHz(rootHz, fifthSemis);

  // Warm lowpass — brightness only when the cloud really moves
  const cutHz = 480 + speedT * 720 + clump * 120;
  // Very slow shimmer from turning (cents-scale, not vibrato buzz)
  const shimmer = 0.0012 + 0.0035 * clamp01(k.meanTurn / Math.PI);

  return { gain, rootHz, thirdHz, fifthHz, cutHz, shimmer };
}

type Partial = { osc: OscillatorNode; gain: GainNode };
type Nodes = {
  parts: Partial[];
  master: GainNode;
  filter: BiquadFilterNode;
  phase: number;
};

function createKinematicsPad(): AudioVoicePlugin {
  let nodes: Nodes | null = null;
  return {
    id: "kinematics_pad",
    label: "Kinematics choir",
    tier: "lab",
    enabled(audio: AudioParams, _sim: SimParams) {
      return audio.enabled && audio.kinematicsVoice;
    },
    start(acx: AudioVoiceContext, _audio: AudioParams) {
      if (nodes) return;
      const { ctx, dest } = acx;
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 700;
      filter.Q.value = 0.35;

      const master = ctx.createGain();
      master.gain.value = 0;
      filter.connect(master);
      master.connect(dest);

      // Root, third, fifth, soft sub-octave — all pure sine
      const parts: Partial[] = [];
      const levels = [0.34, 0.26, 0.22, 0.14];
      for (let i = 0; i < 4; i++) {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = 220;
        const g = ctx.createGain();
        g.gain.value = levels[i]!;
        osc.connect(g);
        g.connect(filter);
        osc.start();
        parts.push({ osc, gain: g });
      }
      nodes = { parts, master, filter, phase: 0 };
    },
    process(frame: AudioFeatureFrame, audio: AudioParams, t: number) {
      if (!nodes) return;
      const m = mapKinematicsPad(frame, audio);
      nodes.phase += frame.dtSec > 0 ? frame.dtSec : 1 / 60;
      // Slow, shallow breath — never aggressive LFO
      const vib = 1 + m.shimmer * Math.sin(nodes.phase * 0.35);

      const freqs = [
        m.rootHz * vib,
        m.thirdHz,
        m.fifthHz,
        m.rootHz * 0.5, // sub for body
      ];
      for (let i = 0; i < nodes.parts.length; i++) {
        const p = nodes.parts[i]!;
        p.osc.frequency.setTargetAtTime(freqs[i] ?? m.rootHz, t, TAU.pitch);
      }
      nodes.master.gain.setTargetAtTime(m.gain, t, TAU.gain);
      nodes.filter.frequency.setTargetAtTime(m.cutHz, t, TAU.filter);
    },
    stop(t: number) {
      if (!nodes) return;
      try {
        nodes.master.gain.setTargetAtTime(0, t, 0.22);
        for (const p of nodes.parts) p.osc.stop(t + 0.7);
      } catch {
        /* */
      }
      nodes = null;
    },
  };
}

export const kinematicsPadVoice: AudioVoicePlugin = createKinematicsPad();
